import React, { useState } from 'react';
import { Share2, Copy, Check, Twitter, Facebook, MessageCircle, Mail, Link2 } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ShareButton = ({ content, size = 'default', showCount = false, onOpenShareModal, className = '' }) => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [shareCount, setShareCount] = useState(content?.share_count || 0);
  const [copied, setCopied] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Only show Share & Earn for real user-uploaded content
  const canEarnFromShare = content?.realContent === true;

  const shareUrl = `${window.location.origin}/content/${content?.id}`;
  const shareTitle = content?.title || 'Check this out!';
  const shareDescription = content?.description || '';

  const trackShare = async (platform) => {
    try {
      await axios.post(
        `${API_URL}/api/social/share/${content?.id}`,
        { platform },
        token ? { headers: { Authorization: `Bearer ${token}` } } : {}
      );
      setShareCount(prev => prev + 1);
    } catch (error) {
      console.error('Error tracking share:', error);
    }
  };

  const handleCopyLink = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      await trackShare('copy');
      toast({
        title: 'Link Copied!',
        description: 'Share link copied to clipboard',
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to copy link',
      });
    }
  };

  const handleSocialShare = (platform, url) => {
    trackShare(platform);
    window.open(url, '_blank', 'width=600,height=400');
    setIsOpen(false);
  };

  const handleShareAndEarn = (e) => {
    e.stopPropagation();
    setIsOpen(false);
    if (onOpenShareModal) {
      onOpenShareModal(content);
    }
  };

  const socialLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${shareTitle} ${shareUrl}`)}`,
    email: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(`Check this out: ${shareUrl}\n\n${shareDescription}`)}`
  };

  const sizeClasses = {
    sm: 'w-8 h-8 p-0',
    default: 'w-10 h-10 p-0',
    lg: 'w-12 h-12 p-0'
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    default: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size={size}
            onClick={(e) => e.stopPropagation()}
            className={`text-white hover:text-white border border-gray-600 hover:border-white rounded-full ${sizeClasses[size]} transition-all duration-200`}
            title="Share"
          >
            <Share2 className={iconSizes[size]} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="end" 
          className="w-56 bg-gray-900 border-gray-700 text-white"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Social Share Options */}
          <DropdownMenuItem 
            onClick={() => handleSocialShare('twitter', socialLinks.twitter)}
            className="cursor-pointer hover:bg-gray-800 focus:bg-gray-800 text-white"
          >
            <Twitter className="w-4 h-4 mr-3 text-blue-400" />
            Share on Twitter
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => handleSocialShare('facebook', socialLinks.facebook)}
            className="cursor-pointer hover:bg-gray-800 focus:bg-gray-800 text-white"
          >
            <Facebook className="w-4 h-4 mr-3 text-blue-600" />
            Share on Facebook
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => handleSocialShare('whatsapp', socialLinks.whatsapp)}
            className="cursor-pointer hover:bg-gray-800 focus:bg-gray-800 text-white"
          >
            <MessageCircle className="w-4 h-4 mr-3 text-green-500" />
            Share on WhatsApp
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => handleSocialShare('email', socialLinks.email)}
            className="cursor-pointer hover:bg-gray-800 focus:bg-gray-800 text-white"
          >
            <Mail className="w-4 h-4 mr-3 text-gray-400" />
            Share via Email
          </DropdownMenuItem>
          
          <DropdownMenuSeparator className="bg-gray-700" />
          
          {/* Copy Link */}
          <DropdownMenuItem 
            onClick={handleCopyLink}
            className="cursor-pointer hover:bg-gray-800 focus:bg-gray-800 text-white"
          >
            {copied ? (
              <><Check className="w-4 h-4 mr-3 text-green-500" /> Copied!</>
            ) : (
              <><Copy className="w-4 h-4 mr-3" /> Copy Link</>
            )}
          </DropdownMenuItem>
          
          {/* Share & Earn Option - Only for real user-uploaded content */}
          {canEarnFromShare && onOpenShareModal && (
            <>
              <DropdownMenuSeparator className="bg-gray-700" />
              <DropdownMenuItem 
                onClick={handleShareAndEarn}
                className="cursor-pointer hover:bg-gray-800 focus:bg-gray-800"
              >
                <div className="flex items-center w-full">
                  <div className="w-4 h-4 mr-3 rounded-full bg-gradient-to-r from-yellow-400 to-purple-500 flex items-center justify-center">
                    <Link2 className="w-3 h-3 text-black" />
                  </div>
                  <span className="bg-gradient-to-r from-yellow-400 to-purple-500 bg-clip-text text-transparent font-medium">
                    Share & Earn
                  </span>
                </div>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {showCount && shareCount > 0 && (
        <span className="text-xs text-gray-400">{shareCount}</span>
      )}
    </div>
  );
};

export default ShareButton;
